import { z } from 'zod';

export const leadStatuses = ['NEW', 'QUALIFIED', 'PROPOSAL', 'WON', 'LOST', 'SPAM'] as const;
export type LeadStatus = (typeof leadStatuses)[number];

export const leadListQuerySchema = z.object({
  status: z.enum(leadStatuses).optional(),
  assignee: z.string().optional(),
  q: z.string().trim().max(100).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export type LeadListQuery = z.infer<typeof leadListQuerySchema>;

export const leadSummarySchema = z.object({
  id: z.string(),
  status: z.enum(leadStatuses),
  name: z.string().nullable(),
  email: z.string().nullable(),
  company: z.string().nullable(),
  assigneeId: z.string().nullable(),
  assigneeName: z.string().nullable(),
  landingName: z.string(),
  landingVersionNumber: z.number().int(),
  source: z.string(),
  spamScore: z.number(),
  createdAt: z.string(),
  piiRedacted: z.boolean(),
  deal: z.object({ amount: z.string(), currency: z.string() }).nullable(),
});
export type LeadSummary = z.infer<typeof leadSummarySchema>;

export const leadListSchema = z.object({
  items: z.array(leadSummarySchema),
  nextCursor: z.string().nullable(),
  counts: z.record(z.string(), z.number()),
});
export type LeadList = z.infer<typeof leadListSchema>;

export const leadEventSchema = z.object({
  id: z.string(),
  type: z.string(),
  actorName: z.string().nullable(),
  payload: z.record(z.string(), z.unknown()),
  occurredAt: z.string(),
});
export type LeadEvent = z.infer<typeof leadEventSchema>;

export const leadDetailSchema = leadSummarySchema.extend({
  message: z.string().nullable(),
  version: z.number().int(),
  landingVersionId: z.string(),
  formVersionId: z.string(),
  locale: z.string(),
  consent: z.object({ analytics: z.boolean(), advertising: z.boolean(), policyVersion: z.string() }),
  firstTouch: z.record(z.string(), z.string()).nullable(),
  lastTouch: z.record(z.string(), z.string()).nullable(),
  spamReasons: z.array(z.string()),
  deal: z.object({ id: z.string(), amount: z.string(), currency: z.string(), proposedAt: z.string(), wonAt: z.string().nullable() }).nullable(),
  events: z.array(leadEventSchema),
});
export type LeadDetail = z.infer<typeof leadDetailSchema>;

export const assignLeadSchema = z.object({ assigneeId: z.string().nullable(), expectedVersion: z.number().int().min(0) });
export const qualifyLeadSchema = z.object({
  outcome: z.enum(['QUALIFIED', 'LOST', 'SPAM']),
  reason: z.string().trim().max(500).optional(),
  expectedVersion: z.number().int().min(0),
});
export type AssignLead = z.infer<typeof assignLeadSchema>;
export type QualifyLead = z.infer<typeof qualifyLeadSchema>;

// Decimal strings preserve cents exactly across JSON and JavaScript clients.
export const proposeLeadSchema = z.object({
  amount: z.string().regex(/^(?:0|[1-9]\d{0,15})\.\d{2}$/, 'Enter an amount with two decimal places').refine((v) => v !== '0.00', 'Amount must be positive'),
  currency: z.string().regex(/^[A-Z]{3}$/, 'Use a three-letter ISO currency code'),
  expectedVersion: z.number().int().min(0),
});
export const winLeadSchema = z.object({ expectedVersion: z.number().int().min(0) });
export type ProposeLead = z.infer<typeof proposeLeadSchema>;
export type WinLead = z.infer<typeof winLeadSchema>;

export const landingOutcomeSchema = z.object({
  landingVersionId: z.string(),
  versionNumber: z.number().int(),
  landingName: z.string(),
  leads: z.number().int(),
  qualified: z.number().int(),
  won: z.number().int(),
  revenue: z.array(z.object({ currency: z.string(), amount: z.string() })),
  spam: z.number().int(),
  lastLeadAt: z.string().nullable(),
});
export type LandingOutcome = z.infer<typeof landingOutcomeSchema>;
